/**
 * Calendar-date rules for DOB, scheduled cash flows, and trust dates.
 * All YMD strings use local calendar (YYYY-MM-DD).
 */

export const DOB_MIN_YMD = "1900-01-01";
export const SCHEDULE_MAX_YMD = "2125-12-31";

export function getTodayYmdLocal() {
  const n = new Date();
  const y = n.getFullYear();
  const m = String(n.getMonth() + 1).padStart(2, "0");
  const d = String(n.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function ymdAddDays(ymd, days) {
  const [y, mo, da] = ymd.split("-").map(Number);
  const dt = new Date(y, mo - 1, da);
  dt.setDate(dt.getDate() + days);
  const ny = dt.getFullYear();
  const nm = String(dt.getMonth() + 1).padStart(2, "0");
  const nd = String(dt.getDate()).padStart(2, "0");
  return `${ny}-${nm}-${nd}`;
}

export function getYesterdayYmd() {
  return ymdAddDays(getTodayYmdLocal(), -1);
}

export function getTomorrowYmd() {
  return ymdAddDays(getTodayYmdLocal(), 1);
}

/** Latest birth date (inclusive) for someone who is at least `years` years old today. */
export function getMaxDobYmdForMinimumAge(years) {
  const t = new Date();
  t.setFullYear(t.getFullYear() - years);
  const y = t.getFullYear();
  const m = String(t.getMonth() + 1).padStart(2, "0");
  const d = String(t.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * @typedef {'dob_primary' | 'dob_non_primary' | 'schedule_future' | 'past_only'} DateFieldConstraint
 */

/**
 * @param {DateFieldConstraint | undefined} constraint
 * @param {{ isPrimary?: boolean }} [_opts]
 * @returns {{ min?: string, max?: string }}
 */
export function getHtmlDateMinMax(constraint, _opts = {}) {
  switch (constraint) {
    case "dob_primary":
      return { min: DOB_MIN_YMD, max: getMaxDobYmdForMinimumAge(18) };
    case "dob_non_primary":
      return { min: DOB_MIN_YMD, max: getYesterdayYmd() };
    case "schedule_future":
      return { min: getTodayYmdLocal(), max: SCHEDULE_MAX_YMD };
    case "past_only":
      return { min: DOB_MIN_YMD, max: getYesterdayYmd() };
    default:
      return {};
  }
}

export function normalizeToYmd(value) {
  if (value === null || value === undefined || value === "") return "";
  const s = String(value).trim();
  const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) return s.slice(0, 10);
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function validateDobYmd(ymd, isPrimary) {
  if (!ymd) return null;
  const norm = normalizeToYmd(ymd);
  if (!norm) return "Please enter a valid date of birth.";
  const today = getTodayYmdLocal();
  if (norm >= today) return "Date of birth must be in the past.";
  if (isPrimary) {
    const maxPrimary = getMaxDobYmdForMinimumAge(18);
    if (norm > maxPrimary) {
      return "Primary account holder must be at least 18 years old.";
    }
  }
  return null;
}

export function isFutureDate(value) {
  if (value == null || String(value).trim() === "") return false;
  const s = String(value).trim();
  const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const parsed = ymd
    ? new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]))
    : new Date(s);
  if (Number.isNaN(parsed.getTime())) return false;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return parsed.getTime() > today.getTime();
}

export function validateDob(value) {
  if (value == null || String(value).trim() === "") return null;
  if (isFutureDate(value)) return "Date of birth cannot be in the future.";
  return null;
}

export function validateScheduleFutureYmd(ymd, fieldLabel = "Start date") {
  if (!ymd) return null;
  const norm = normalizeToYmd(ymd);
  if (!norm) return `Please enter a valid ${fieldLabel.toLowerCase()}.`;
  const today = getTodayYmdLocal();
  if (norm < today) {
    return `${fieldLabel} cannot be in the past.`;
  }
  return null;
}

export function validatePastOnlyYmd(ymd, fieldLabel = "Date") {
  if (!ymd) return null;
  const norm = normalizeToYmd(ymd);
  if (!norm) return `Please enter a valid ${fieldLabel.toLowerCase()}.`;
  const today = getTodayYmdLocal();
  if (norm >= today) {
    return `${fieldLabel} must be a past date.`;
  }
  return null;
}

/**
 * @param {string} val raw or YYYY-MM-DD
 * @param {DateFieldConstraint | undefined} constraint
 * @param {string} label
 * @param {{ isPrimary?: boolean }} [_opts]
 */
export function getDateConstraintError(val, constraint, label, _opts = {}) {
  if (!constraint || !val) return null;
  const norm = normalizeToYmd(val);
  switch (constraint) {
    case "dob_primary":
      return validateDobYmd(norm, true);
    case "dob_non_primary":
      return validateDobYmd(norm, false);
    case "schedule_future":
      return validateScheduleFutureYmd(norm, label);
    case "past_only":
      return validatePastOnlyYmd(norm, label);
    default:
      return null;
  }
}

export function ymdToLocalDate(ymd) {
  const norm = normalizeToYmd(ymd);
  if (!norm) return null;
  const [y, m, d] = norm.split("-").map(Number);
  return new Date(y, m - 1, d, 12, 0, 0, 0);
}

export function getDatePickerBoundsForConstraint(constraint, opts = {}) {
  const { min, max } = getHtmlDateMinMax(constraint, opts);
  return {
    minDate: min ? ymdToLocalDate(min) : undefined,
    maxDate: max ? ymdToLocalDate(max) : undefined,
  };
}
